import { formatCurrency } from '@urbanx/agx-ui-components'
import {
  Deposit,
  DepositCondition,
  DepositConditionType,
  DepositDayType,
  FormCondition,
  FormConditionCondition,
  FormConditionConditionType,
  FormConditionDayType,
} from '@urbanx/agx.campaigns.client'
import { AgxBadge, AgxSpacer } from '@urbanx/agx.ui-components2'
import dayjs from 'dayjs'
import { useMemo } from 'react'

const getConditionTypeText = (
  conditionType: FormConditionConditionType | DepositConditionType | undefined
) => {
  switch (conditionType) {
    case FormConditionConditionType.Contract:
    case DepositConditionType.Contract:
      return 'contract date'
    case FormConditionConditionType.Finance:
    case DepositConditionType.Finance:
      return 'finance being satisfied'
    case FormConditionConditionType.Unconditional:
    case DepositConditionType.Unconditional:
      return 'unconditional date'
    default:
      return ''
  }
}

const isBusinessDays = (
  dayType: FormConditionDayType | DepositDayType | undefined | null
): boolean => {
  return (
    dayType === FormConditionDayType.BusinessDays ||
    dayType === DepositDayType.BusinessDays
  )
}

const ConditionDateText = ({
  condition,
  isDeposit = false,
  depositAmount,
  specialConditionIdToClauseNumberMap,
}: {
  condition: FormCondition | Deposit
  isDeposit?: boolean
  depositAmount?: number
  specialConditionIdToClauseNumberMap: {
    [key: string]: string
  }
}) => {
  const dueOrPayable = useMemo(
    () => (isDeposit ? 'Payable' : 'Due'),
    [isDeposit]
  )

  const amountPrefix = useMemo(() => {
    if (!isDeposit || depositAmount === undefined) return ''
    return `${formatCurrency(depositAmount)} `
  }, [isDeposit, depositAmount])

  const referenceDateText = useMemo(() => {
    return dayjs(condition.referenceDate).format('DD/MM/YYYY')
  }, [condition.referenceDate])

  if (
    condition.condition ===
    FormConditionCondition.UponSatisfactionOfSpecialCondition
  ) {
    const clauseNumber =
      specialConditionIdToClauseNumberMap[condition.specialConditionId ?? ''] ??
      ''

    const specialConditionText = `special condition ${clauseNumber}`

    if (condition.daysFromDate && condition.daysFromDate > 0) {
      const dayTypeText =
        condition.dayType === FormConditionDayType.BusinessDays
          ? 'business '
          : ''
      return (
        <>
          {amountPrefix}
          {dueOrPayable} {condition.daysFromDate} {dayTypeText}day(s) from
          satisfaction of {specialConditionText}
        </>
      )
    }

    return (
      <AgxSpacer
        direction="horizontal"
        gap={'3xs'}
        contentJustification="center"
        contentAlignment="center"
      >
        {amountPrefix}
        {dueOrPayable} on
        <AgxBadge
          size="md"
          type="colourfill"
          colour="grey"
          text={`satisfaction of ${specialConditionText}`}
        ></AgxBadge>
      </AgxSpacer>
    )
  } else if (condition.condition === FormConditionCondition.OnSpecificDate) {
    return (
      <>
        {amountPrefix}
        {dueOrPayable} on {referenceDateText}
      </>
    )
  } else if (
    condition.condition === FormConditionCondition.NdaysFromDate ||
    condition.condition === FormConditionCondition.AsPerContract
  ) {
    const dateText = getConditionTypeText(condition?.conditionType)

    // If daysFromDate is set and greater than 0, show "X day(s) from"
    if (condition.daysFromDate && condition.daysFromDate > 0) {
      const dayTypeText =
        condition.dayType === FormConditionDayType.BusinessDays
          ? 'business '
          : ''
      return (
        <>
          {amountPrefix}
          {dueOrPayable} {condition.daysFromDate} {dayTypeText}day(s) from{' '}
          <AgxBadge size="md" type="colourfill" colour="grey" text={dateText} />
        </>
      )
    }

    // Otherwise, show "on [date]"
    return (
      <>
        {amountPrefix}
        {dueOrPayable} on{' '}
        <AgxBadge size="md" type="colourfill" colour="grey" text={dateText} />
      </>
    )
  } else if (
    condition.condition === FormConditionCondition.FinanceBeingSatisfied
  ) {
    if (condition.daysFromDate && condition.daysFromDate > 0) {
      const dayTypeText = isBusinessDays(condition.dayType) ? 'business ' : ''
      return (
        <>
          {amountPrefix}
          {dueOrPayable} {condition.daysFromDate} {dayTypeText}day(s) from{' '}
          <AgxBadge
            size="md"
            type="colourfill"
            colour="grey"
            text="finance being satisfied"
          />
        </>
      )
    }
    return (
      <>
        {amountPrefix}
        {dueOrPayable}{' '}
        <AgxBadge
          size="md"
          type="colourfill"
          colour="grey"
          text="upon finance being satisfied"
        />
      </>
    )
  } else if (
    condition.condition === FormConditionCondition.BuildingAndPestBeingSatisfied
  ) {
    if (condition.daysFromDate && condition.daysFromDate > 0) {
      const dayTypeText = isBusinessDays(condition.dayType) ? 'business ' : ''
      return (
        <>
          {amountPrefix}
          {dueOrPayable} {condition.daysFromDate} {dayTypeText}day(s) from{' '}
          <AgxBadge
            size="md"
            type="colourfill"
            colour="grey"
            text="building and pest being satisfied"
          />
        </>
      )
    }
    return (
      <>
        {amountPrefix}
        {dueOrPayable}{' '}
        <AgxBadge
          size="md"
          type="colourfill"
          colour="grey"
          text="upon building and pest being satisfied"
        />
      </>
    )
  } else if (
    condition.condition === FormConditionCondition.OnDate ||
    condition.condition === DepositCondition.OnDate
  ) {
    // If referenceDate is set, show the actual date instead of "contract date"
    if (condition.referenceDate) {
      return (
        <>
          {amountPrefix}
          {dueOrPayable} on{' '}
          <AgxBadge
            size="md"
            type="colourfill"
            colour="grey"
            text={referenceDateText}
          />
        </>
      )
    }

    const dateText = getConditionTypeText(condition?.conditionType)

    return (
      <>
        {amountPrefix}
        {dueOrPayable} on{' '}
        <AgxBadge size="md" type="colourfill" colour="grey" text={dateText} />
      </>
    )
  } else if (
    condition.condition === undefined &&
    condition.conditionType !== undefined
  ) {
    // Handle deposits with conditionType but no condition value set
    const dateText = getConditionTypeText(condition?.conditionType)

    return (
      <>
        {amountPrefix}
        {dueOrPayable} on{' '}
        <AgxBadge size="md" type="colourfill" colour="grey" text={dateText} />
      </>
    )
  } else {
    return null
  }
}

export default ConditionDateText
